'use client';

/**
 * AiHealthBanner — a small, friendly indicator that surfaces AI slowness or an
 * outage so a generation failure is never a silent "nothing happens". Polls the
 * passive /api/ai/health (no test calls, so it never burns a rate-limited free
 * quota). Renders nothing when AI is healthy. A dismiss SNOOZES it for 10 minutes
 * (remembered per browser) so it never nags on every page. Deliberately worded in
 * plain, non-alarming language for non-technical clients.
 */

import { useEffect, useState } from 'react';

const SNOOZE_KEY = 'aiHealthBannerSnoozeUntil';
const SNOOZE_MS = 10 * 60 * 1000; // stay hidden for 10 min after a dismiss

export default function AiHealthBanner() {
    const [h, setH] = useState(null);
    const [snoozed, setSnoozed] = useState(true); // assume hidden until storage is read (no flash)

    useEffect(() => {
        try { const until = parseInt(localStorage.getItem(SNOOZE_KEY) || '0', 10); setSnoozed(Date.now() < until); }
        catch { setSnoozed(false); }
        let alive = true;
        const check = async () => {
            try { const r = await fetch('/api/ai/health'); const j = await r.json(); if (alive && j.success) setH(j); } catch {}
        };
        check();
        const t = setInterval(check, 45000);
        return () => { alive = false; clearInterval(t); };
    }, []);

    if (!h || h.status === 'ok' || snoozed) return null;
    const noProvider = h.status !== 'down'; // 'down' = a request failed; otherwise = not configured
    const dismiss = () => { try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)); } catch {} setSnoozed(true); };
    // Only surface a stored error if it's already client-friendly (no dev jargon).
    // Also hide free-pool / model-routing internals (model slugs, HTTP codes,
    // "not supported", the OmniRoute "oc/…" chain) — clients should never see
    // "Model north-mini-code-free is not supported"; they get the friendly line
    // below instead, which points them at the real fix (add a free AI key).
    const rawErr = String(h.lastError || '');
    const isJargon = /ollama|timeoutms|abort|econn|fetch failed|instant ai|not supported|unknown model|unsupported|oc\/|:free|http\s?\d{3}|\[\d{3}\]|\b\d{3}\b|model\b/i.test(rawErr);
    const friendlyErr = rawErr && !isJargon ? rawErr.slice(0, 180) : '';
    return (
        <div style={{ position: 'fixed', bottom: 16, left: 16, zIndex: 9999, maxWidth: 340, background: 'var(--bg-card)', border: '1px solid var(--amber)', borderLeft: '4px solid var(--amber)', borderRadius: 10, padding: '12px 14px', color: 'var(--text-primary)', boxShadow: 'var(--shadow-lg)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div>
                    <div style={{ fontWeight: 800, fontSize: 13 }}>{noProvider ? 'Set up AI to unlock AI features' : 'The AI is a bit slow right now'}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 4, lineHeight: 1.5 }}>
                        {noProvider
                            ? (h.localReason || 'Download a free local model in Settings → AI Models (works offline, no key), or add your own AI key in Settings → AI.')
                            : (friendlyErr || 'The local AI is busy, so generating may take a little longer or need a retry. A smaller model or your own AI key in Settings makes this faster.')}
                    </div>
                    <a href={noProvider ? '/models' : '/settings'} style={{ fontSize: 12.5, color: 'var(--blue)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3, display: 'inline-block', marginTop: 6 }}>{noProvider ? 'Open AI Models' : 'Open Settings → AI'}</a>
                </div>
                <button onClick={dismiss} title="Dismiss" style={{ background: 'transparent', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>✕</button>
            </div>
        </div>
    );
}
